"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BranchTable, { BranchRow } from "../components/branch-table";
import BranchCreateModal, { BranchFormValues } from "../components/branch-create-modal";
import BranchStatusConfirmModal from "../components/branch-status-confirm-modal";
import BranchDetailModal from "../components/branch-detail-modal";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import BranchService, { Branch, BranchStatusCode } from "../services/branch-service";
import { phoneRegExp } from "../../../constants/regex";
import { compareAsc } from "date-fns";
import { removeVietnameseFromString } from "../../../utils/helpers";

/** Bỏ dấu + lowercase để tìm kiếm không dấu (gõ "cao thang" ra "Cao Thắng"). */
const normalize = (v: string) => removeVietnameseFromString(v || "").toLowerCase();

export const BranchIndexPage: React.FC = () => {
  const [rows, setRows] = useState<BranchRow[]>([]);
  const [tab, setTab] = useState<"active" | "inactive">("active");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState<BranchRow | null>(null);
  const [pendingStatus, setPendingStatus] = useState<{ row: BranchRow; target: "active" | "inactive" } | null>(null);
  const [detailRow, setDetailRow] = useState<BranchRow | null>(null);
  const [detailData, setDetailData] = useState<Branch | undefined>();

  const { fetch: listBranches, response: branchResponse, loading: loadingList } = BranchService.list();
  const { fetch: createBranch, response: createResponse, loading: creating } = BranchService.create();
  const { fetch: updateBranch, response: updateResponse, loading: updating } = BranchService.update();
  const { fetch: changeBranchStatus, response: changeStatusResponse, loading: changingStatus } = BranchService.changeStatus();
  const { fetch: fetchBranchDetail, response: branchDetailResponse, loading: loadingDetail } = BranchService.detail();

  const listRef = useRef(listBranches);
  useEffect(() => {
    listRef.current = listBranches;
  }, [listBranches]);

  const load = useCallback(async () => {
    const res = await listRef.current();
    if (res && isSuccess(res)) {
      const apiData = Array.isArray(res.data) ? res.data : [];
      setRows(
        apiData.map((r: any) => ({
          id: r.id,
          company_id: r.company_id,
          name: r.name,
          phone: r.phone ?? "",
          description: r.description ?? "",
          address: r.address ?? "",
          user_count: r.user_count ?? 0,
          created_at: r.created_at,
          updated_at: r.updated_at,
          statusCode: Number(r.status) as BranchStatusCode,
          isFactory: Number(r.is_factory) === 1,
          latitude: r.latitude ?? null,
          longitude: r.longitude ?? null,
          attendanceRadiusM: r.attendance_radius_m ?? null,
        }))
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // BE trả toàn bộ danh sách (không phân trang) nên lọc ngay trên client theo tên / SĐT / địa chỉ / mô tả.
  const filteredRows = useMemo(() => {
    const q = normalize(search.trim());
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.phone, r.address, r.description].some((field) => normalize(field).includes(q))
    );
  }, [rows, search]);

  const activeRows = useMemo(
    () => filteredRows.filter((r) => r.statusCode === BranchStatusCode.ACTIVE),
    [filteredRows]
  );
  const inactiveRows = useMemo(
    () => filteredRows.filter((r) => r.statusCode === BranchStatusCode.INACTIVE),
    [filteredRows]
  );

  const onCreate = async (v: BranchFormValues): Promise<boolean> => {
    const payload = {
      code: v.code.trim().toUpperCase(),
      name: v.name.trim(),
      address: v.address?.trim() ?? "",
      phone: v.phone?.trim() ?? "",
      company_id: v.companyId,
      description: v.description?.trim() ?? "",
      // Form tạo không hỏi trạng thái — chi nhánh mới luôn hoạt động.
      status: BranchStatusCode.ACTIVE,
      is_factory: v.isFactory ? 1 : 0,
      latitude: v.latitude,
      longitude: v.longitude,
      attendance_radius_m: v.attendanceRadiusM,
    };
    const res = await createBranch(payload);
    if (res && isSuccess(res)) {
      showToast({ message: "Tạo chi nhánh thành công", type: "success" });
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể tạo chi nhánh", type: "error" });
    return false;
  };

  const onEditSubmit = async (v: BranchFormValues): Promise<boolean> => {
    if (!editRow) return false;
    const payload = {
      code: v.code.trim().toUpperCase(),
      name: v.name.trim(),
      phone: v.phone?.trim() ?? "",
      address: v.address?.trim() ?? "",
      description: v.description?.trim() ?? "",
      status: v.isActive ? BranchStatusCode.ACTIVE : BranchStatusCode.INACTIVE,
      is_factory: v.isFactory ? 1 : 0,
      latitude: v.latitude,
      longitude: v.longitude,
      attendance_radius_m: v.attendanceRadiusM,
    };
    const res = await updateBranch(editRow.id, payload);
    if (res && isSuccess(res)) {
      showToast({ message: "Cập nhật chi nhánh thành công", type: "success" });
      setEditRow(null);
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể cập nhật chi nhánh", type: "error" });
    return false;
  };

  const handleViewDetail = async (row: BranchRow) => {
    setDetailRow(row);
    setDetailData(undefined);
    try {
      const res = await fetchBranchDetail(row.id);
      if (res && isSuccess(res)) setDetailData(res.data);
      else setDetailRow(null);
    } catch {
      setDetailRow(null);
    }
  };

  const handleCloseDetailModal = () => {
    setDetailRow(null);
    setDetailData(undefined);
  };

  const onRequestDeactivate = (row: BranchRow) =>
    setPendingStatus({ row, target: "inactive" });
  const onRequestActivate = (row: BranchRow) =>
    setPendingStatus({ row, target: "active" });

  const onConfirmChangeStatus = async () => {
    if (!pendingStatus) return;
    const next =
      pendingStatus.target === "active"
        ? BranchStatusCode.ACTIVE
        : BranchStatusCode.INACTIVE;
    const res = await changeBranchStatus(pendingStatus.row.id, { status: next });
    if (res && isSuccess(res)) {
      showToast({
        message:
          pendingStatus.target === "active"
            ? "Đã kích hoạt chi nhánh"
            : "Đã tạm ngưng chi nhánh",
        type: "success",
      });
      setPendingStatus(null);
      void load();
      return;
    }
    showToast({
      message: res?.message ?? "Không thể cập nhật trạng thái",
      type: "error",
    });
    setPendingStatus(null);
  };

  return (
    <div className="">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-green-600 pl-3">
          Danh mục chi nhánh
        </h1>
        <button
          onClick={() => setOpen(true)}
          className="group inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 transition-all hover:bg-green-700 hover:-translate-y-0.5"
          >
             <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
              <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
            </svg>
            <span>THÊM CHI NHÁNH</span>
          </button>
      </div>

      {/* Tabs + Search */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 bg-slate-50 px-6 py-3 rounded-lg">
        <div className="flex flex-wrap gap-6">
          <button
            onClick={() => setTab("active")}
            className={`relative pb-2 text-sm font-semibold uppercase tracking-wide ${
              tab === "active"
                ? "text-green-600 border-b-2 border-green-500"
                : "text-slate-400 hover:text-slate-600"
            }`}
          >
            ĐANG HOẠT ĐỘNG{" "}
            <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-600 rounded-full">
              {activeRows.length}
            </span>
          </button>
          <button
            onClick={() => setTab("inactive")}
            className={`relative pb-2 text-sm font-semibold uppercase tracking-wide ${
              tab === "inactive"
                ? "text-green-600 border-b-2 border-green-500"
                : "text-slate-400 hover:text-slate-600"
            }`}
          >
            TẠM NGƯNG{" "}
            <span className="ml-2 px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full">
              {inactiveRows.length}
            </span>
          </button>
        </div>

        <div className="relative w-full sm:w-[300px]">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm kiếm chi nhánh..."
            className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-4 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all"
          />
        </div>
      </div>

      {/* Table */}
      <div className="mt-4">
        {tab === "active" ? (
          <BranchTable
            rows={activeRows}
            isLoading={loadingList}
            startIndex={1}
            onEdit={setEditRow}
            onDeactivate={onRequestDeactivate}
            onViewDetail={handleViewDetail}
          />
        ) : (
          <BranchTable
            rows={inactiveRows}
            isLoading={loadingList}
            startIndex={1}
            onEdit={setEditRow}
            onActivate={onRequestActivate}
            onViewDetail={handleViewDetail}
          />
        )}
      </div>

      {/* Modals */}
      {open && (
        <BranchCreateModal
          onClose={() => setOpen(false)}
          onSubmit={onCreate}
          isSubmitting={creating}
        />
      )}
      {editRow && (
        <BranchCreateModal
          onClose={() => setEditRow(null)}
          onSubmit={onEditSubmit}
          isSubmitting={updating}
          titleText="Cập nhật chi nhánh"
          submitText="Cập nhật"
          showActiveToggle
          initialValues={{
            name: editRow.name,
            phone: editRow.phone,
            address: editRow.address,
            description: editRow.description,
            isActive: editRow.statusCode === BranchStatusCode.ACTIVE,
            isFactory: !!editRow.isFactory,
            latitude: editRow.latitude ?? null,
            longitude: editRow.longitude ?? null,
            attendanceRadiusM: editRow.attendanceRadiusM ?? null,
          }}
        />
      )}
      {pendingStatus && (
        <BranchStatusConfirmModal
          mode={pendingStatus.target === "inactive" ? "deactivate" : "activate"}
          branchName={pendingStatus.row.name}
          onCancel={() => setPendingStatus(null)}
          onConfirm={onConfirmChangeStatus}
          isProcessing={changingStatus}
        />
      )}
      {detailRow && detailData && (
        <BranchDetailModal
          name={detailRow.name}
          data={detailData}
          loading={loadingDetail}
          onClose={handleCloseDetailModal}
        />
      )}
    </div>
  );
};

export default BranchIndexPage;
